import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { SvgArrow } from 'components/Svgs';
import { intlMessageShape } from 'constants/propTypes';
import { bem } from 'helpers/class-name';

import './ExpandableSection.scss';

/**
 * Visual variants: block modifier (`modifier(variant)`) adds a modifier class that can be referenced in SCSS, for example, `&--gray`,
 * typically background-color per variant. To add one: extend EXPANDABLE_SECTION_VARIANTS,
 * add styles in ExpandableSection.scss, extend PropTypes, add a ComponentExamples demo.
 */
export const EXPANDABLE_SECTION_VARIANTS = Object.freeze({
  DEFAULT: 'default',
  GRAY: 'gray',
});

const VARIANT_KEYS = Object.values(EXPANDABLE_SECTION_VARIANTS);

let nextInstanceId = 1;

const { block, element, modifier } = bem('expandable-section');

function ExpandableSection({
  children,
  className,
  defaultOpen = false,
  id,
  isOpen: isOpenProp,
  onToggle,
  title,
  variant = EXPANDABLE_SECTION_VARIANTS.DEFAULT,
}) {
  const fallbackIdRef = useRef(null);
  if (fallbackIdRef.current === null) {
    fallbackIdRef.current = `expandable-section-${nextInstanceId}`;
    nextInstanceId += 1;
  }

  const rootId = id ?? fallbackIdRef.current;
  const panelId = `${rootId}-panel`;
  const titleId = `${rootId}-title`;

  const isControlled = typeof onToggle === 'function' && typeof isOpenProp === 'boolean';
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);

  const open = isControlled ? isOpenProp : uncontrolledOpen;

  const handleToggle = () => {
    const next = !open;
    if (isControlled) {
      onToggle(next);
    } else {
      setUncontrolledOpen(next);
    }
  };

  return (
    <section className={classNames(block(), className, modifier(variant))} id={rootId}>
      <button
        aria-controls={panelId}
        aria-expanded={open}
        className={element('trigger')}
        id={`${rootId}-trigger`}
        type="button"
        onClick={handleToggle}>
        <span className={element('title')} id={titleId}>
          {title}
        </span>
        <span aria-hidden className={element('caret')}>
          <SvgArrow direction={open ? 'up' : 'down'} theme="dark" />
        </span>
      </button>
      <div className={element('panel', { open })} id={panelId} aria-hidden={!open}>
        <div className={element('panel-inner')}>
          <div className={element('panel-content')}>{children}</div>
        </div>
      </div>
    </section>
  );
}

ExpandableSection.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  defaultOpen: PropTypes.bool,
  id: PropTypes.string,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  title: PropTypes.shape(intlMessageShape).isRequired,
  variant: PropTypes.oneOf(VARIANT_KEYS),
};

export default ExpandableSection;
